import { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Helmet } from "react-helmet-async";
import { Home, ChevronRight } from "lucide-react";
import BackToTop from "@/components/BackToTop";

const pageNames: Record<string, string> = {
  "/services": "Services",
  "/about": "About",
  "/case-studies": "Case Studies",
  "/blog": "Blog",
  "/contact": "Contact",
  "/ofsted-reports": "Ofsted Reports",
};

interface PageLayoutProps {
  children: ReactNode;
  title?: string;
  description?: string;
  jsonLd?: object;
  ogImage?: string;
}

const SITE_URL = "https://qa1st.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

const PageLayout = ({ children, title, description, jsonLd, ogImage }: PageLayoutProps) => {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const currentPageName = pageNames[location.pathname];
  const canonical = `${SITE_URL}${location.pathname}`;
  const image = ogImage || DEFAULT_OG_IMAGE;

  return (
    <>
      <Helmet>
        {title && <title>{title}</title>}
        {description && <meta name="description" content={description} />}
        <link rel="canonical" href={canonical} />
        {title && <meta property="og:title" content={title} />}
        {description && <meta property="og:description" content={description} />}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:site_name" content="QA 1st" />
        <meta property="og:image" content={image} />
        <meta name="twitter:card" content="summary_large_image" />
        {title && <meta name="twitter:title" content={title} />}
        {description && <meta name="twitter:description" content={description} />}
        <meta name="twitter:image" content={image} />
        {jsonLd && (
          <script type="application/ld+json">
            {JSON.stringify(jsonLd)}
          </script>
        )}
      </Helmet>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        {!isHome && (
          <div className="bg-card border-b border-border">
            <div className="container-narrow px-4 md:px-8 py-3 flex items-center gap-2 text-sm">
              <Link to="/" className="text-muted-foreground hover:text-accent transition-colors inline-flex items-center gap-1">
                <Home className="h-3.5 w-3.5" />
                <span>Home</span>
              </Link>
              {currentPageName && (
                <>
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" />
                  <span className="text-foreground font-medium">{currentPageName}</span>
                </>
              )}
            </div>
          </div>
        )}
        <main className="flex-1">{children}</main>
        <Footer />
        <BackToTop />
      </div>
    </>
  );
};

export default PageLayout;
